/**
 * Slide Builder patches riding the Work harness stream.
 *
 * In Evidence chat the backend streams `slide_update` / `slide_delete` chunks
 * directly. In Work the same editors (`edit_slide`, `add_slides_to_presentation`,
 * `delete_slide`) run as harness tools, and each returns its Evidence payload
 * unchanged as `ToolResult.data`, which the harness forwards on the
 * `tool_result_blocks` stream event under `provider_metadata.data`.
 *
 * This module is the headless half: it recognises those payloads by shape
 * (`type: 'slide_update' | 'slide_delete'`, or the `add_slides` envelope
 * `{ slides: [...] }`) and returns them in order. The app decides what to do
 * with them — patch the open deck's atom — at the single stream owner.
 */

import type { LLMStreamEvent } from '../../../contracts/harness.types';
import { isRecord } from '../../../contracts/parse-utils';

/** Structurally compatible with the Evidence `SlideUpdateChunk`. */
export interface SlideUpdatePayload {
  type: 'slide_update';
  status: 'completed' | 'render_failed' | 'error';
  presentation_id: string;
  slide_id: string;
  slide_index?: number;
  image_url?: string;
  pptx_url?: string;
  title?: string;
  error?: string;
  /** Sibling slides re-rendered by the same edit (e.g. a references deck). */
  updated_reference_slides?: SlideReferenceUpdate[];
}

export interface SlideReferenceUpdate {
  slide_id: string;
  slide_index: number;
  title: string;
  image_url?: string;
}

export interface SlideDeletePayload {
  type: 'slide_delete';
  status: string;
  presentation_id: string;
  slide_id: string;
}

export type SlideStreamUpdate = SlideUpdatePayload | SlideDeletePayload;

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const asReferenceUpdate = (value: unknown): SlideReferenceUpdate | undefined => {
  if (!isRecord(value)) return undefined;
  const slideId = str(value.slide_id);
  if (!slideId || typeof value.slide_index !== 'number') return undefined;
  return {
    slide_id: slideId,
    slide_index: value.slide_index,
    title: typeof value.title === 'string' ? value.title : '',
    ...(str(value.image_url) ? { image_url: str(value.image_url) } : {}),
  };
};

const asSlideUpdate = (value: unknown): SlideUpdatePayload | undefined => {
  if (!isRecord(value) || value.type !== 'slide_update') return undefined;
  const status = value.status;
  if (status !== 'completed' && status !== 'render_failed' && status !== 'error') {
    return undefined;
  }
  const presentationId = str(value.presentation_id);
  const slideId = str(value.slide_id);
  if (!presentationId || !slideId) return undefined;
  return {
    type: 'slide_update',
    status,
    presentation_id: presentationId,
    slide_id: slideId,
    ...(typeof value.slide_index === 'number' ? { slide_index: value.slide_index } : {}),
    ...(str(value.image_url) ? { image_url: str(value.image_url) } : {}),
    ...(str(value.pptx_url) ? { pptx_url: str(value.pptx_url) } : {}),
    ...(typeof value.title === 'string' ? { title: value.title } : {}),
    ...(str(value.error) ? { error: str(value.error) } : {}),
    ...(Array.isArray(value.updated_reference_slides)
      ? {
          updated_reference_slides: value.updated_reference_slides.flatMap((ref) => {
            const parsed = asReferenceUpdate(ref);
            return parsed ? [parsed] : [];
          }),
        }
      : {}),
  };
};

const asSlideDelete = (value: unknown): SlideDeletePayload | undefined => {
  if (!isRecord(value) || value.type !== 'slide_delete') return undefined;
  const presentationId = str(value.presentation_id);
  const slideId = str(value.slide_id);
  const status = str(value.status);
  if (!presentationId || !slideId || !status) return undefined;
  return {
    type: 'slide_delete',
    status,
    presentation_id: presentationId,
    slide_id: slideId,
  };
};

const fromData = (data: unknown): SlideStreamUpdate[] => {
  const single = asSlideUpdate(data) ?? asSlideDelete(data);
  if (single) return [single];
  // `add_slides_to_presentation` returns `{ success, slides: [slide_update, …] }`.
  // The backend only ever sets `success: true` (failures raise and arrive as an
  // errored block); each slide's own `status` carries its outcome. A `false`
  // envelope is still refused so nothing patches the deck from a failed call.
  if (isRecord(data) && Array.isArray(data.slides) && data.success !== false) {
    return data.slides.flatMap((slide) => {
      const update = asSlideUpdate(slide);
      return update ? [update] : [];
    });
  }
  return [];
};

/**
 * Slide Builder patches carried by one harness stream event, in block order.
 * Empty for every event that is not `tool_result_blocks` or carries none.
 */
export const extractSlideStreamUpdates = (event: LLMStreamEvent): SlideStreamUpdate[] => {
  if (event.type !== 'tool_result_blocks' || !event.tool_result_blocks?.length) return [];
  return event.tool_result_blocks.flatMap((block) =>
    block.is_error ? [] : fromData(block.provider_metadata?.data)
  );
};
